<template>
  <div :class="['upload-wrapper', { 'with-content': hasContent }]">
    <div v-if="isLoading" class="upload-loading-overlay">
      <VProgressRing />
    </div>
    <input
      type="file"
      style="display: none;"
      :id="id"
      ref="fileInput"
      @change="handleFileUpload"
      :accept="acceptTypes"
      :multiple="multiple"
      :disabled="isLoading"
    />
    <div class="slot-content-wrapper" @click.stop="() => { !showIcons ? openFileInput() : 0 }">
      <slot></slot>
      <div class="icons-wrapper" v-if="showIcons && !isViewOnlyMode">
        <VIcon @click.stop="openFileInput" name="edit-line" class="icon"></VIcon>
        <VIcon @click.stop="$emit('delete-media')" name="delete-line" connotation="alert" class="icon trash"></VIcon>
      </div>
    </div>
    <div v-if="uploadURLEnabled" class="upload-url-row outer" :style="uploadURLRowStyle">
      <div class="or-label">or enter URL</div>
      <VSelect v-if="allowDynamicURL" :disabled="disableInputType || disableDynamicURL" v-model="urlType" aria-label="buttonType" placeholder="URL Type" appearance="ghost" class="vivid-select">
        <VOption value="static" text="Static URL"></VOption>
        <VOption value="dynamic" text="Dynamic URL"></VOption>
      </VSelect>
      <template v-if="urlType === 'static'">
        <div class="upload-url-row" :style="innerURLRowStyle">
          <VTextField
            type="text" 
            aria-label="paste url" 
            :readonly="disableInputType"
            v-model="uploadFileUrl"
            placeholder="Paste URL here..."
            appearance="ghost"
            class="vivid-input"
          ></VTextField>
          <button @click.stop="uploadURLFiles" :disabled="disableInputType" class="btn-primary">
            Add
          </button>
        </div>
      </template>
      <template v-else>
        <VSelect v-if="askDynamicUrlType" :disabled="disableInputType || disableDynamicURL" v-model="dynamicUrlTypeComputed" aria-label="buttonType" placeholder="URL Type" appearance="ghost" class="vivid-select url-type-select" required>
          <VOption value="IMAGE" text="Image"></VOption>
          <VOption value="VIDEO" text="Video"></VOption>
        </VSelect>
        <div class="upload-url-row" :style="innerURLRowStyle">
          <custom-field-input v-model="uploadFileUrl" :readOnly="disableInputType" placeholder="Enter URL here..." />
          <button @click.stop="uploadDynamicURLFiles" :disabled="disableInputType || !uploadFileUrl" class="btn-primary">
            Add
          </button>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import apiInterceptor from '@/shared/apiInterceptor';
import axios from 'axios';
import CustomFieldInput from '@/components/CustomFieldInput.vue'
import { useUIStore } from '@/stores/ui';
import { environment } from '@/shared/env.js';
import { validateRcsUrl } from '@/helpers/rcs-url.helpers.js';

export default {
  components: {
    CustomFieldInput
  },
  emits:['update:loading', 'send-upload-data', 'url-uploaded', 'url-type-changed', 'delete-media'],
  props: {
    type: {
      type: String,
      required: true,
      validator: value => ['image', 'video', 'document', 'image/video', 'image/video/document'].includes(value)
    },
    multiple: {
      type: Boolean,
      default: false
    },
    maxFileSizeAllowed: {
      type: Number
    },
    loading: {
      type: Boolean,
      default: false
    },
    maximumFiles: {
      type: Number,
      default: 5
    },
    allowedImageTypes: {
      type: String
    },
    allowedVideoTypes: {
      type: String
    },
    allowedDocumentTypes: {
      type: String
    },
    id: {
      type: String,
      default: 'fileInput'
    },
    isViewOnlyMode: {
      type: Boolean,
      default: false,
    },
    uploadURLEnabled: {
      type: Boolean,
      default: false,
    },
    media_url: {
      type: [String, Array],
      default: ''
    },
    uploaded_url_type: {
      type: String,
      default: 'static'
    },
    meta: {
      type: Object,
      default(rawProps) {
        return {
          is_export: rawProps.is_export || false,
          is_broadcast: rawProps.is_broadcast || false,
          is_redact: rawProps.is_redact || false,
          is_public: rawProps.is_public || true,
          conversation_id: rawProps.conversation_id || undefined,
          channel: rawProps.channel || undefined,
        }
      }
    },
    allowDynamicURL: {
      type: Boolean,
      default: false
    },
    disableDynamicURL: {
      type: Boolean,
      default: false
    },
    showIcons: {
      type: Boolean,
      default: false
    },
    selectStyle: {
      type: String
    },
    uploadURLRowStyle: {
      type: String
    },
    innerURLRowStyle: {
      type: String
    },
    askDynamicUrlType: {
      type: Boolean,
      default: false
    },
    dynamicUrlType: {
      type: String,
      default: 'IMAGE'
    }
  },
  data() {
    return {
      uploadFileUrl: '',
      urlType: ''
    };
  },
  mounted() {
    this.urlType = this.uploaded_url_type || 'static';
    if (this.urlType === 'dynamic' && this.media_url) {
      this.uploadFileUrl = this.media_url;
    }
  },
  computed: {
    acceptTypes() {
      var imageTypes, videoTypes, documentTypes;

      if(!this.allowedImageTypes?.length && this.meta.channel) {
        if(this.meta.channel === 'googlercs') imageTypes = 'image/png, image/jpeg, image/jpg, image/gif';
        else imageTypes = 'image/png, image/jpeg, image/jpg';
      } else imageTypes = this.allowedImageTypes;

       if(!this.allowedVideoTypes?.length && this.meta.channel) {
        if(this.meta.channel === 'googlercs') videoTypes = 'video/mp4, video/mpeg, video/m4v, video/mpeg4, video/webm';
        else videoTypes = 'video/mp4';
      } else videoTypes = this.allowedVideoTypes;

      if(!this.allowedDocumentTypes?.length && this.meta.channel) {
        if(this.meta.channel === 'googlercs') documentTypes = 'application/pdf';
        else documentTypes = 'application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, text/plain, application/pdf, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.presentationml.presentation';
      } else documentTypes = this.allowedDocumentTypes;

      switch (this.type) {
        case 'image':
          return imageTypes;
        case 'video':
          return videoTypes;
        case 'document':
          return documentTypes;
        case 'image/video':
          return imageTypes + ", " + videoTypes;
        case 'image/video/document': 
          return imageTypes + ", " + videoTypes + ", " + documentTypes;
        default:
          return "";
      }
    },
    isLoading: {
      get() {
        return this.loading;
      },
      set(value) {
        this.$emit('update:loading', value);
      }
    },
    disableInputType(){
			return this.isViewOnlyMode || this.isLoading ;
		},
    hasContent() {
      return this.uploadURLEnabled || !!this.$slots.default;
    },
    dynamicUrlTypeComputed: {
      get() {
        return this.dynamicUrlType;
      },
      set(value) {
        this.$emit('update:dynamicUrlType', value);
      }
    }
  },
  beforeUnmount() {
    this.isLoading = false;
  },
   watch: {
    urlType(newVal) {
      this.$emit('url-type-changed', newVal);
    },
  },
  methods: {
    openFileInput() {
      if(!this.$route.query?.automation_test && !this.isViewOnlyMode){
        this.$refs.fileInput.click();
      }
    },
    clearFileInput() {
      this.$refs.fileInput.value = null;
    },
    isValidFileSize(size) {
      return !this.maxFileSizeAllowed || (this.maxFileSizeAllowed > size / (1024*1024));
    },
    isValidFileType(contentType) {
      return this.acceptTypes?.split(', ').includes(contentType);
    },
    uploadMedia(file) {
			return new Promise(async (resolve, reject) => {
				try {
          const fileName = file.name;
          const fileSize = file.size;
          const mimeType = file.type;

					let fd = new FormData();

					fd.append('channel', this.meta.channel);
					fd.append('conversation_id', this.meta.conversation_id);
					fd.append('is_public', this.meta.is_public);
					fd.append('is_redact', this.meta.is_redact);
					fd.append('is_broadcast', this.meta.is_broadcast);
					fd.append('is_export', this.meta.is_export);

					fd.append('mime_type', mimeType);
					fd.append('file_name', fileName);
					fd.append('file_size', fileSize);

          const reader = new FileReader();

          reader.onload = async (e) => {
            const f1000bytes = new Uint8Array(e.target.result).slice(0, 1000);
            fd.append('file_bytes', f1000bytes);

            apiInterceptor.post(environment('VUE_APP_API_URL') + "shared-services/v1/utils/generate-upload-url", fd).then((uploadResponse) => {
              const signedURL = uploadResponse.signed_url;
              const headers = { headers: uploadResponse.headers };
              
              axios.put(signedURL, file, headers).then((uploadSendResponse) => {
                if(uploadSendResponse.status === 200) {
                  resolve({
                    file_url: uploadResponse.file_url,
                    file_name: fileName,
                    mime_type: mimeType,
                    url: uploadResponse.file_url,
                    filename: fileName,
                    filesize: fileSize,
                    mimetype: mimeType
                  });
                } else {
                  useUIStore().updateToast({
                    open: true,
                    message: "Something went wrong",
                    type: "danger"
                  })
                  reject(false);
                }
              });
            }).catch((e) => {
              reject(e);
            });
          }
          reader.readAsArrayBuffer(file);					
				} catch (error) {
					reject(error);
				}
			})
		},
    async handleFileUpload(event) {
      const files = event.target.files;
      const sendArr = [];
      this.isLoading = true;
      try {
        if (files?.length) {
          let totalFilesToBeSent = files.length;

          if (this.multiple && totalFilesToBeSent > this.maximumFiles) return useUIStore().updateToast({ open: true, message: `You can't upload more than ${this.maximumFiles} ${this.type}s!`, type: "danger", });

          while(totalFilesToBeSent > 0) {
            const fileToSend = files[totalFilesToBeSent - 1];
            const fileName = fileToSend.name;
            const fileSize = fileToSend.size;
            const mimeType = fileToSend.type;
  
            if(!this.isValidFileType(mimeType)) return useUIStore().updateToast({
              open: true,
              message: `${fileName}: Only ${this.acceptTypes} file are allowed`,
              type: "danger"
            })
  
            if(!this.isValidFileSize(fileSize)) return useUIStore().updateToast({
              open: true,
              message: `${fileName}: Max file size allowed is: ${this.maxFileSizeAllowed} MB`,
              type: "danger"
            })
  
            const uploadSendResponseData = await this.uploadMedia(fileToSend);
            sendArr.push(uploadSendResponseData);
            
            totalFilesToBeSent--;
          }
          this.urlType = 'static';
          this.uploadFileUrl = '';
          return this.$emit('send-upload-data', sendArr);
        } else {
          useUIStore().updateToast({ open: true, message: "Please select at least one file to upload!", type: "danger" });
        }
      } catch (error) {
        console.error("Exception", error);
        useUIStore().updateToast({ open: true, message: error.message || error.errorMessage || "Something went wrong!", type: "danger" });
      } finally {
        this.isLoading = false;
      }
    },
    async uploadURLFiles() {
      try {
        if (!this.uploadFileUrl) {
          useUIStore().updateToast({ open: true, message: "Please add valid URL and try again!", type: "danger", duration: 2000 });
          return;
        }
        this.isLoading = true;
        const urlUploadResponse = await apiInterceptor.post( environment("VUE_APP_API_URL") + "shared-services/v1/utils/url-upload", {
          url: this.uploadFileUrl,
          channel: this.meta.channel,
          conversation_id: this.meta.conversation_id,
          is_broadcast: this.meta.is_broadcast,
          is_public: this.meta.is_public
        });
        if(this.acceptTypes?.includes(urlUploadResponse.mime_type)) {
          useUIStore().updateToast({ open: true, message: "File added successfully!", type: "success" });
          if(this.askDynamicUrlType) {
            this.$emit("url-uploaded", { ...urlUploadResponse, url_type: 'static', dynamicUrlType: this.dynamicUrlTypeComputed });
          } else {
            this.$emit("url-uploaded", { ...urlUploadResponse, url_type: 'static' });
          }
        } else {
          useUIStore().updateToast({ open: true, message: "Invalid file extension", type: "danger" });
        }
      } catch (error) {
        useUIStore().updateToast({
          open: true,
          message: error.message || "Something went wrong",
          type: "danger"
        })
      } finally{
        this.isLoading = false;
        this.uploadFileUrl = "";
      }
    },
    uploadDynamicURLFiles() {
      if (!this.uploadFileUrl) {
        useUIStore().updateToast({ open: true, message: "Please add valid URL and try again!", type: "danger", duration: 2000 });
        return;
      }
      if (this.meta?.channel === 'googlercs' && !validateRcsUrl(this.uploadFileUrl, true)) {
        useUIStore().updateToast({
          open: true,
          message: "Please enter a valid RFC 3986 URL. Dynamic URLs may include <variable_name> placeholders, but static URL parts must be valid.",
          type: "danger",
          duration: 5000
        });
        return;
      }
      useUIStore().updateToast({ open: true, message: "Dynamic URL added successfully!", type: "success" });
      if(this.askDynamicUrlType){
        this.$emit("url-uploaded", { file_url: this.uploadFileUrl, url_type: 'dynamic', dynamicUrlType: this.dynamicUrlTypeComputed });
      } else {
        this.$emit("url-uploaded", { file_url: this.uploadFileUrl, url_type: 'dynamic' });
      }
    }
  }
};
</script>


<style scoped lang="scss">
.upload-wrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  border-radius: 8px;
  margin: 0;
  padding: 0;
  position: relative;
  &.with-content {
    margin: 10px 0;
    padding: 15px;
    width: 100%;
    border: 1px dotted var(--gray-01);
  }
  &:hover {
    border-color: var(--purple);
  }
  .upload-loading-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(255, 255, 255, 0.85);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    z-index: 10;
    border-radius: 8px;
    span {
      font-size: 13px;
      color: var(--gray-04);
    }
  }
  .upload-url-row {
    &.outer {
      margin-top: 10px;
      position: relative;
      padding: 15px 0 10px 0;
      border-top: 1px solid var(--gray-01);
    }
    display: flex;
    flex-direction: row;
    align-content: center;
    align-items: center;
    justify-content: center;
    gap: 10px;
    width: 100%;
    .or-label {
      position: absolute;
      top: 0px;
      font-size: 14px;
      text-align: center;
      margin-top: -10px;
      background-color: var(--white);
      padding: 0 5px;
    }
    .url-type-select {
      min-height: 40px;
    }
  }
  .btn-primary {
    background: var(--purple);
    color: white;
    border: none;
    padding: 8px 16px;
    border-radius: 5px;
    cursor: pointer;
    font-size: 14px;
    height: 40px;
    white-space: nowrap;
  }
  .btn-primary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .slot-content-wrapper {
    display: flex;
    flex-direction: column;
    width: 100%;
    position: relative;
    .icons-wrapper {
      display: none;
      flex-direction: row;
      align-items: center;
      position: absolute;
      gap: 10px;
      top: 0;
      right: 0;
      padding: 5px;
      background-color: var(--white);
      border-radius: 0 0 0 10px;
      z-index: 9;
      .icon {
        height: 20px;
        width: 20px;
        cursor: pointer;
        color: var(--purple);
        border-right: 1px solid var(--gray-01);
        padding: 0 5px;
        &:last-child {
          border-right: none;
        }
      }
      .trash {
        color: var(--danger);
      }
    }
    &:hover {
      .icons-wrapper {
        display: flex;
      }
    }
  }
}
</style>
